import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from "@mui/material";
import { collection, onSnapshot, query, orderBy, limit, where } from "firebase/firestore";
import { db } from "../firebase";
import { TIER_COLORS } from "../theme";
import { AttachMoney, TrendingUp, AccountBalance } from "@mui/icons-material";

export function PayoutsView() {
  const [jobs, setJobs] = useState<any[]>([]);
  const [techs, setTechs] = useState<any[]>([]);

  useEffect(() => {
    const u1 = onSnapshot(
      query(collection(db, "jobs"), where("status", "==", "completed"), orderBy("updatedAt", "desc"), limit(100)),
      snap => setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const u2 = onSnapshot(collection(db, "technicians"), snap =>
      setTechs(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { u1(); u2(); };
  }, []);

  const totalRevenue = jobs.reduce((s, j) => s + (j.totalCharge || 0), 0);
  const totalPlatformFees = jobs.reduce((s, j) => s + (j.platformFee || 0), 0);
  const totalTechPayouts = jobs.reduce((s, j) => s + (j.techPayout || 0), 0);

  // Per-tech earnings summary
  const techEarnings = techs.map(tech => {
    const techJobs = jobs.filter(j => j.techId === tech.id);
    return {
      ...tech,
      completedJobs: techJobs.length,
      totalEarned: techJobs.reduce((s, j) => s + (j.techPayout || 0), 0),
      platformContrib: techJobs.reduce((s, j) => s + (j.platformFee || 0), 0),
    };
  }).filter(t => t.completedJobs > 0).sort((a, b) => b.totalEarned - a.totalEarned);

  const fmt = (ts: any) => {
    if (!ts?.toDate) return "—";
    return ts.toDate().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <Box>
      {/* Summary cards */}
      <Grid container spacing={3} mb={3}>
        {[
          { label: "Total Revenue", value: `$${totalRevenue.toFixed(2)}`, icon: <AttachMoney />, color: "#22c55e" },
          { label: "Platform Fees", value: `$${totalPlatformFees.toFixed(2)}`, icon: <TrendingUp />, color: "#FF8C00" },
          { label: "Tech Payouts", value: `$${totalTechPayouts.toFixed(2)}`, icon: <AccountBalance />, color: "#8b5cf6" },
        ].map(s => (
          <Grid size={{ xs:12, md:4 }} key={s.label}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                      {s.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} mt={0.5}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{jobs.length} completed jobs</Typography>
                  </Box>
                  <Box sx={{ bgcolor: s.color + "22", borderRadius: 2, p: 1.5, color: s.color }}>
                    {s.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Per-tech summary */}
        <Grid size={{ xs:12, md:5 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Earnings by Tech</Typography>
              {techEarnings.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No completed jobs yet</Typography>
              ) : techEarnings.map(tech => (
                <Box key={tech.id} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, pb: 2, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{tech.displayName}</Typography>
                      <Chip label={tech.tier} size="small" sx={{ bgcolor: TIER_COLORS[tech.tier]+"22", color: TIER_COLORS[tech.tier], fontWeight: 700, fontSize: 10, textTransform: "capitalize" }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">{tech.completedJobs} jobs · {((1 - (tech.commissionRate || 0.18)) * 100).toFixed(0)}% keep rate</Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body2" fontWeight={700} color="success.main">${tech.totalEarned.toFixed(2)}</Typography>
                    <Typography variant="caption" color="secondary.main">+${tech.platformContrib.toFixed(2)} platform</Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent completed jobs */}
        <Grid size={{ xs:12, md:7 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Recent Completed Jobs</Typography>
            </CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Job","Total","Platform","Tech Payout","Commission","Time"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: "text.secondary", fontSize: 11, textTransform: "uppercase" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.slice(0,15).map(job => (
                    <TableRow key={job.id} hover>
                      <TableCell><Typography variant="caption" fontFamily="monospace">{job.id.slice(0,6)}…</Typography></TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>${(job.totalCharge||0).toFixed(2)}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="secondary.main">${(job.platformFee||0).toFixed(2)}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="success.main">${(job.techPayout||0).toFixed(2)}</Typography></TableCell>
                      <TableCell><Chip label={`${((job.commissionRateAtBooking||0.18)*100).toFixed(0)}%`} size="small" sx={{ fontWeight: 700, fontSize: 11 }} /></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{fmt(job.updatedAt)}</Typography></TableCell>
                    </TableRow>
                  ))}
                  {jobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                        No completed jobs yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
